import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { audits as auditsApi, users as usersApi } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Send, AlertTriangle, DollarSign, Save } from "lucide-react";

export default function MultaDialog({
  open,
  onOpenChange,
  auditId,
  question,
  category,
  template,
  store,
  initialAmount = "",
  initialReason = "",
  onConfirmed,
}) {
  const { t, i18n } = useTranslation();
  const [amount, setAmount] = useState(initialAmount);
  const [reason, setReason] = useState(initialReason);
  const [observation, setObservation] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAmount(initialAmount);
    setReason(initialReason);
    setObservation("");
    setSelected(new Set());
    usersApi
      .list()
      .then((list) => {
        // Filter to active users that can act on findings (admins, auditors, gerentes)
        setRecipients(
          (list || []).filter((u) =>
            u.active && ["admin", "auditor", "gerente", "direccion"].includes(u.role),
          ),
        );
      })
      .catch((e) => {
        console.error(e);
        setRecipients([]);
      });
  }, [open, initialAmount, initialReason]);

  const generateReason = async () => {
    setAiBusy(true);
    try {
      const r = await auditsApi.multaAiReason(auditId, {
        question: question?.question || "",
        category: category || "",
        template: template || "",
        store: store || "",
        auditor_observation: observation || "",
        language: i18n.language,
      });
      setReason(r.reason || "");
    } catch (e) {
      const msg = e?.response?.data?.detail || t("multa.ai_error");
      toast.error(msg);
    } finally {
      setAiBusy(false);
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async (notify) => {
    if (notify && selected.size === 0) {
      toast.error(t("multa.no_recipients_selected"));
      return;
    }
    setSaving(true);
    try {
      // 1) Notify recipients (if requested)
      if (notify) {
        const ids = Array.from(selected);
        const r = await auditsApi.multaNotify(auditId, {
          question_id: question?.id || "",
          question: question?.question || "",
          category: category || "",
          template: template || "",
          multa_amount: parseFloat(amount) || null,
          multa_reason: reason || "",
          recipient_user_ids: ids,
        });
        toast.success(t("multa.notified", { n: r.sent || ids.length }));
      }
      // 2) Always return the captured payload to parent for storage
      onConfirmed && (await onConfirmed({
        amount: parseFloat(amount) || null,
        reason: reason || "",
      }));
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const recipientsList = useMemo(() => recipients, [recipients]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="multa-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <AlertTriangle className="text-red-500" size={20} />
            {t("multa.title")}
          </DialogTitle>
          <p className="text-xs text-stone-500">{t("multa.subtitle")}</p>
        </DialogHeader>

        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          <strong>{t("common.question")}:</strong> {question?.question}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <div className="md:col-span-1">
            <label className="canales-overline">{t("multa.amount")} ({t("multa.currency")})</label>
            <div className="relative mt-1">
              <DollarSign size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={t("multa.amount_placeholder")}
                inputMode="decimal"
                className="pl-7"
                data-testid="multa-amount"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="canales-overline">{t("multa.observation_label")}</label>
            <Input
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              placeholder={t("multa.observation_placeholder")}
              className="mt-1"
              data-testid="multa-observation"
            />
          </div>
        </div>

        <div className="mt-3">
          <div className="flex items-center justify-between">
            <label className="canales-overline">{t("multa.reason_label")}</label>
            <Button
              size="sm"
              type="button"
              variant="outline"
              onClick={generateReason}
              disabled={aiBusy}
              className="gap-1.5 h-7 text-xs"
              data-testid="multa-ai-button"
            >
              {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {aiBusy ? t("multa.ai_generating") : reason ? t("multa.ai_regenerate") : t("multa.ai_generate")}
            </Button>
          </div>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("multa.reason_placeholder")}
            className="mt-1 min-h-[110px]"
            data-testid="multa-reason"
          />
        </div>

        <div className="mt-4">
          <label className="canales-overline">{t("multa.recipients_label")}</label>
          {recipientsList.length === 0 ? (
            <p className="text-xs text-stone-400 mt-2">{t("multa.no_recipients_available")}</p>
          ) : (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1" data-testid="multa-recipients">
              {recipientsList.map((u) => (
                <label
                  key={u.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm cursor-pointer transition-colors ${
                    selected.has(u.id)
                      ? "border-[#1E3A8A] bg-[#E5EBFA]"
                      : "border-stone-200 hover:bg-stone-50"
                  }`}
                  data-testid={`recipient-${u.email}`}
                >
                  <Checkbox
                    checked={selected.has(u.id)}
                    onCheckedChange={() => toggle(u.id)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{u.name}</div>
                    <div className="text-[11px] text-stone-500 truncate">
                      {u.role} · {u.email}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saving}
            className="gap-1.5"
            data-testid="multa-save-only"
          >
            <Save size={14} /> {t("multa.saved_only")}
          </Button>
          <Button
            onClick={() => handleSave(true)}
            disabled={saving || selected.size === 0}
            className="brand-button-accent gap-1.5"
            data-testid="multa-save-and-notify"
          >
            <Send size={14} /> {t("multa.save_and_notify")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
